import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties, FocusEvent } from "react";
import { createPortal } from "react-dom";
import type { ScheduleWeather, UserPreferences } from "../types";
import {
  formatProjectedCloud,
  formatProjectedHumidity,
  formatProjectedTemp,
  formatProjectedWind,
  formatScheduleTempRange,
} from "../utils/scheduleWeather";

interface Props {
  weather: ScheduleWeather | null | undefined;
  units?: UserPreferences["units"];
}

export default function ScheduleWeatherChip({
  weather,
  units = "standard",
}: Props) {
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const closeTimerRef = useRef<number | null>(null);
  const [open, setOpen] = useState(false);
  const [panelPosition, setPanelPosition] = useState({ top: 0, left: 0 });

  const activeWeather = weather || null;

  const mainLabel = useMemo(() => {
    if (!activeWeather) return "No weather";
    return (
      formatProjectedTemp(activeWeather.controlWeather, units) ||
      formatScheduleTempRange(activeWeather.officialForecast, units) ||
      "No weather"
    );
  }, [activeWeather, units]);

  const subLabel = useMemo(() => {
    if (!activeWeather) return null;
    return (
      formatProjectedCloud(activeWeather.controlWeather, activeWeather.officialForecast) ||
      activeWeather.officialForecast?.skyLabel ||
      activeWeather.officialForecast?.skyLowLabel ||
      null
    );
  }, [activeWeather]);

  useEffect(() => {
    if (!open) return;

    const updatePosition = () => {
      const rect = anchorRef.current?.getBoundingClientRect();
      const panelRect = panelRef.current?.getBoundingClientRect();
      if (!rect || !panelRect) return;

      const gap = 10;
      const margin = 12;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      const centeredLeft = rect.left + rect.width / 2 - panelRect.width / 2;
      const nextLeft = Math.min(
        Math.max(centeredLeft, margin),
        viewportWidth - panelRect.width - margin,
      );

      const belowTop = rect.bottom + gap;
      const aboveTop = rect.top - panelRect.height - gap;
      const fitsBelow = belowTop + panelRect.height <= viewportHeight - margin;
      const fitsAbove = aboveTop >= margin;

      let nextTop = belowTop;
      if (!fitsBelow && fitsAbove) {
        nextTop = aboveTop;
      } else if (!fitsBelow) {
        nextTop = Math.max(
          margin,
          Math.min(belowTop, viewportHeight - panelRect.height - margin),
        );
      }

      setPanelPosition({ top: nextTop, left: nextLeft });
    };

    const raf = window.requestAnimationFrame(updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [open, weather]);

  useEffect(() => {
    return () => {
      if (closeTimerRef.current != null) {
        window.clearTimeout(closeTimerRef.current);
      }
    };
  }, []);

  const handleOpen = () => {
    if (closeTimerRef.current != null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setOpen(true);
  };

  const scheduleClose = () => {
    if (closeTimerRef.current != null) {
      window.clearTimeout(closeTimerRef.current);
    }
    closeTimerRef.current = window.setTimeout(() => {
      setOpen(false);
      closeTimerRef.current = null;
    }, 100);
  };

  const handleBlur = (event: FocusEvent<HTMLDivElement>) => {
    if (event.currentTarget.contains(event.relatedTarget)) return;
    scheduleClose();
  };

  if (!weather) {
    return <span style={styles.empty}>--</span>;
  }

  const resolvedWeather = activeWeather || weather;

  const panel =
    open && typeof document !== "undefined"
      ? createPortal(
          <div
            ref={panelRef}
            style={{
              ...styles.panel,
              top: `${panelPosition.top}px`,
              left: `${panelPosition.left}px`,
            }}
            onMouseEnter={handleOpen}
            onMouseLeave={scheduleClose}
          >
          <div style={styles.panelSection}>
            <div style={styles.panelHeader}>
              <div style={styles.panelEyebrow}>Projected Weather</div>
              <div style={styles.panelTitle}>
                {formatProjectedTemp(resolvedWeather.controlWeather, units) ||
                  formatScheduleTempRange(resolvedWeather.officialForecast, units) ||
                  "Forecast pending"}
              </div>
              <div style={styles.panelSummary}>
                {resolvedWeather.controlWeather?.summary ||
                  resolvedWeather.officialForecast?.summary ||
                  "Stable weekly test target derived from the forecast."}
              </div>
            </div>

            <div style={styles.grid}>
              <MetricRow
                label="Clouds"
                value={formatProjectedCloud(
                  resolvedWeather.controlWeather,
                  resolvedWeather.officialForecast,
                )}
              />
              <MetricRow
                label="Humidity"
                value={formatProjectedHumidity(
                  resolvedWeather.controlWeather,
                  resolvedWeather.officialForecast,
                )}
              />
              <MetricRow
                label="Wind"
                value={formatProjectedWind(resolvedWeather.controlWeather, units)}
              />
              <MetricRow
                label="Time"
                value={resolvedWeather.sessionTimeLabel || "No time"}
              />
            </div>
          </div>
          </div>,
          document.body,
        )
      : null;

  return (
    <Fragment>
      <div
        ref={anchorRef}
        style={styles.wrapper}
        onMouseEnter={handleOpen}
        onMouseLeave={scheduleClose}
        onFocus={handleOpen}
        onBlur={handleBlur}
        onClick={(event) => event.stopPropagation()}
        tabIndex={0}
        role="button"
        aria-label="Show schedule weather detail"
      >
        <div style={styles.chip}>
          <div style={styles.chipTop}>
            <span style={styles.chipEyebrow}>Projected Weather</span>
          </div>
          <div style={styles.chipMain}>{mainLabel}</div>
          <div style={styles.chipSub}>{subLabel || "Forecast"}</div>
        </div>
      </div>
      {panel}
    </Fragment>
  );
}

function MetricRow({ label, value }: { label: string; value: string | null }) {
  return (
    <div style={styles.metricCard}>
      <div style={styles.metricLabel}>{label}</div>
      <div style={styles.metricValue}>{value || "--"}</div>
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  wrapper: {
    display: "inline-flex",
    position: "relative",
    outline: "none",
  },
  chip: {
    display: "flex",
    flexDirection: "column",
    gap: "0.2rem",
    minWidth: "8.3rem",
    padding: "0.58rem 0.72rem",
    borderRadius: "16px",
    border: "1px solid rgba(148, 163, 184, 0.18)",
    background:
      "linear-gradient(180deg, rgba(18, 26, 40, 0.98), rgba(11, 18, 32, 0.97))",
    boxShadow: "0 10px 24px rgba(2, 6, 23, 0.18)",
  },
  chipTop: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: "0.4rem",
  },
  chipEyebrow: {
    fontFamily: "var(--font-body)",
    color: "var(--text-secondary)",
    fontSize: "0.82rem",
    fontWeight: 500,
  },
  chipMain: {
    color: "var(--text-strong)",
    fontSize: "0.92rem",
    fontWeight: 800,
    lineHeight: 1.05,
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
  },
  chipSub: {
    color: "var(--text-secondary)",
    fontSize: "0.8125rem",
    lineHeight: 1.25,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  panel: {
    position: "fixed",
    zIndex: 120,
    width: "20.75rem",
    borderRadius: "20px",
    border: "1px solid rgba(148, 163, 184, 0.2)",
    background:
      "linear-gradient(180deg, rgba(9, 14, 24, 0.995), rgba(6, 10, 18, 0.99))",
    boxShadow: "0 28px 80px rgba(2, 6, 23, 0.56)",
    padding: "0.85rem",
    display: "flex",
    flexDirection: "column",
    gap: "0.75rem",
  },
  panelSection: {
    display: "flex",
    flexDirection: "column",
    gap: "0.7rem",
    padding: "0.8rem",
    borderRadius: "16px",
    border: "1px solid rgba(148, 163, 184, 0.1)",
    background: "rgba(17, 24, 39, 0.98)",
  },
  panelHeader: {
    display: "flex",
    flexDirection: "column",
    gap: "0.22rem",
    minWidth: 0,
  },
  panelEyebrow: {
    fontFamily: "var(--font-body)",
    color: "var(--text-secondary)",
    fontSize: "0.82rem",
    fontWeight: 500,
  },
  panelTitle: {
    color: "var(--text-strong)",
    fontSize: "1rem",
    fontWeight: 800,
    lineHeight: 1.08,
  },
  panelSummary: {
    color: "var(--text-secondary)",
    fontSize: "0.8125rem",
    lineHeight: 1.32,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: "0.6rem 0.7rem",
  },
  metricCard: {
    display: "flex",
    flexDirection: "column",
    gap: "0.26rem",
    minHeight: "3.7rem",
    padding: "0.62rem 0.68rem",
    borderRadius: "13px",
    border: "1px solid rgba(148, 163, 184, 0.1)",
    background: "rgba(12, 19, 31, 1)",
  },
  metricLabel: {
    fontFamily: "var(--font-body)",
    color: "var(--text-secondary)",
    fontSize: "0.82rem",
    fontWeight: 500,
  },
  metricValue: {
    color: "var(--text-strong)",
    fontSize: "0.8125rem",
    fontWeight: 700,
    lineHeight: 1.28,
  },
  note: {
    color: "var(--text-muted)",
    fontSize: "0.8125rem",
    lineHeight: 1.3,
    padding: "0 0.2rem",
  },
  empty: {
    color: "var(--text-muted)",
    fontSize: "0.8125rem",
  },
};
